import React, { useEffect, useState, useRef } from "react";
import {
  IonContent,
  IonPage,
  IonText,
  IonButton,
  IonIcon,
  IonSpinner,
  useIonAlert,
} from "@ionic/react";
import { closeCircleOutline } from 'ionicons/icons'; 
import axios from 'axios';
import { Geolocation } from '@ionic-native/geolocation';
import env from '../env.json'; 

import HeaderSos from "../components/HeaderSos";
import Footer from "../components/Footer";
import ButtonSos3 from "./ButtonSos3"; 

import './SOS.css';
import '../theme/variables.css';

interface props {
  netState: boolean;
}

type NewSosFlowState = 'INITIAL_VIEW' | 'COUNTDOWN_VIEW' | 'SUBMITTING_VIEW';
const COUNTDOWN_SECONDS = 8;

const CIRCLE_SIZE = 140;
const CIRCLE_STROKE_WIDTH = 12;
const CIRCLE_RADIUS = (CIRCLE_SIZE - CIRCLE_STROKE_WIDTH) / 2;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const SOS: React.FC<props> = ({ netState }) => {
  const [presentAlert] = useIonAlert();
  const [currentView, setCurrentView] = useState<NewSosFlowState>('INITIAL_VIEW');
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(true);
  const [activeSosExists, setActiveSosExists] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(COUNTDOWN_SECONDS);
  
  useEffect(() => { 
    const checkInitialSosStatus = () => {
        const existingStatus = localStorage.getItem('status');
        if (existingStatus) setActiveSosExists(true); else setActiveSosExists(false);
        setIsLoadingStatus(false);
    };
    checkInitialSosStatus();
  }, []);

  useEffect(() => { 
    let timerId: NodeJS.Timeout | undefined = undefined;
    if (currentView === 'COUNTDOWN_VIEW' && countdown > 0) {
      timerId = setTimeout(() => {
        setCountdown(prevCountdown => prevCountdown - 1);
      }, 1000);
    } else if (currentView === 'COUNTDOWN_VIEW' && countdown === 0) {
      console.log("Contador a cero, enviando SOS...");
      handleSendSosData();
    }
    return () => { if (timerId) clearTimeout(timerId); };
  }, [currentView, countdown]);


  const goToInitialView = () => { 
    setCurrentView('INITIAL_VIEW');
    setCountdown(COUNTDOWN_SECONDS);
  };

  const goToCountdownView = () => { 
    if (!netState) { presentAlert({ header: 'Sin Conexión', message: 'No tienes conexión.', buttons: ['OK']}); return; }
    setCurrentView('COUNTDOWN_VIEW');
    setCountdown(COUNTDOWN_SECONDS);
  };

  const handleSendSosData = async () => { 
    if (currentView === 'SUBMITTING_VIEW') return; 

    if (!netState) { presentAlert({ header: 'Sin Conexión', message: 'No se puede enviar SOS.', buttons: ['OK']}); setCurrentView('COUNTDOWN_VIEW'); return; }
    setCurrentView('SUBMITTING_VIEW');
    const profileString = localStorage.getItem('profile');
    let userId = null;
    if (profileString) { try {userId = JSON.parse(profileString).id} catch(e){console.error("Error parseando perfil", e)}}
    if (!userId) { presentAlert({ header: 'Error', message: 'Usuario no encontrado.', buttons: ['OK']}); setCurrentView('INITIAL_VIEW'); return;}
    let latitude = 0, longitude = 0;
    try { 
      const positionOptions = { timeout: 15000, enableHighAccuracy: true };
      const position = await Geolocation.getCurrentPosition(positionOptions);
      latitude = position.coords.latitude; longitude = position.coords.longitude;
    } catch (geoError: any) { presentAlert({ header: 'Advertencia Ubicación', message: `No se pudo obtener ubicación. (Error: ${geoError.message})`, buttons: ['Entendido']});}
    const generarNumero = (min: number, max: number): string => (Math.floor(Math.random() * (max - min + 1)) + min).toString();
    const ticketNumber = generarNumero(100000, 999999);
    const token = localStorage.getItem('token');
    if (!token) { presentAlert({ header: 'Error Auth', message: 'No autenticado.', buttons: ['OK']}); setCurrentView('INITIAL_VIEW'); return; }
    
    const descriptionToSend = "Seguridad"; 
    
    const payload = { 
        number: ticketNumber, 
        user_id: userId, 
        description: descriptionToSend, 
        lat: latitude, 
        lng: longitude, 
        ubicacion: "Pendiente de validar"
    };

    console.log("Enviando Payload a /api/sos:", payload);
    const apiUrl = `${env.REACT_APP_URL}/api/sos`; 
    try {
      const response = await axios.post(apiUrl, payload, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}, timeout: 20000 });
      if (response.status >= 200 && response.status < 300) {
        localStorage.setItem('status', 'success');
        presentAlert({ header: 'SOS Enviado', message: 'Solicitud SOS enviada.', buttons: ['OK'] });
        setActiveSosExists(true);
      } else { throw new Error(response.data?.message || `Error API: ${response.status}`); }
    } catch (apiError: any) {
      let errorMsg = 'No se pudo enviar SOS.'; 
      if(apiError.response){ errorMsg = apiError.response.data?.message || `Error: ${apiError.response.status}`}
      else if(apiError.request){ errorMsg = 'Sin respuesta del servidor.'}
      else {errorMsg = apiError.message || errorMsg}
      presentAlert({ header: 'Error al Enviar SOS', message: errorMsg, buttons: ['OK']});
      setCurrentView('COUNTDOWN_VIEW');
    }
  };

  const renderInitialSosView = () => { 
    return (
      <div className="sos-container centered-container initial-view-styling">
        <IonText className="initial-prompt-heading"><h1>¿Está en Emergencia?</h1></IonText>
        <IonText className="initial-prompt-subtext"><p>Presione el botón. Tiene {COUNTDOWN_SECONDS} segundos para actuar y enviar su ubicación.</p></IonText>
        <div className="initial-sos-button-container">
          <div className="sos-pulse-ring ring-1"></div> <div className="sos-pulse-ring ring-2"></div> <div className="sos-pulse-ring ring-3"></div>
          <IonButton className="initial-sos-button" onClick={goToCountdownView} shape="round">
            SOS
          </IonButton>
        </div>
      </div>
    );
  };

  const renderCountdownActionView = () => { 
    const progress = countdown / COUNTDOWN_SECONDS;
    const dashOffset = CIRCLE_CIRCUMFERENCE * (1 - progress);
    return (
      <div className="sos-container centered-container countdown-view-container">
        <div className="countdown-circle-container">
          <svg className="countdown-svg" width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`} > <circle className="countdown-circle-bg" cx={CIRCLE_SIZE / 2} cy={CIRCLE_SIZE / 2} r={CIRCLE_RADIUS} strokeWidth={CIRCLE_STROKE_WIDTH} /> <circle className="countdown-circle-progress" cx={CIRCLE_SIZE / 2} cy={CIRCLE_SIZE / 2} r={CIRCLE_RADIUS} strokeWidth={CIRCLE_STROKE_WIDTH} strokeDasharray={CIRCLE_CIRCUMFERENCE} strokeDashoffset={dashOffset} transform={`rotate(-90 ${CIRCLE_SIZE / 2} ${CIRCLE_SIZE / 2})`} /> <text className="countdown-circle-text" x="50%" y="50%" dy=".3em" textAnchor="middle" > {countdown} </text> <text className="countdown-circle-subtext" x="50%" y="50%" dy="1.8em" textAnchor="middle" > SEG </text> </svg>
        </div>
        <IonText className="sos-prompt-text" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
          <h2>¿Qué está pasando?</h2>
          <p style={{fontSize: '0.9em', color: '#555'}}>Su alerta se enviará en cuanto finalice el conteo o presione "Enviar SOS Ahora".</p>
        </IonText>
        
        {countdown > 0 && ( 
          <div className="countdown-actions">
            <IonButton 
                className="send-sos-now-button" 
                color="danger"
                shape="round" 
                expand="block"
                onClick={() => handleSendSosData()}
            >
                Enviar SOS Ahora
            </IonButton>
            <IonButton 
                className="cancel-sos-button" 
                fill="outline" 
                color="medium"
                shape="round" 
                expand="block"
                onClick={goToInitialView} 
            >
                Cancelar
            </IonButton>
          </div>
        )}
        {countdown === 0 && currentView === 'COUNTDOWN_VIEW' && (
             <IonText color="medium" style={{marginTop: '1rem'}}><p>Enviando SOS...</p></IonText>
        )}
      </div>
    );
  };
  
  const renderSubmittingView = () => { return (<div className="sos-container centered-container"> <IonSpinner name="crescent" /> <IonText><p>Enviando información SOS...</p></IonText> </div> ); };
  
  if (isLoadingStatus) {  return ( <IonPage> <HeaderSos pageName="SOS" /> <IonContent className="ion-padding"> <div className="sos-container centered-container"> <IonSpinner name="crescent" /> <IonText><p>Verificando...</p></IonText> </div> </IonContent> <Footer /> </IonPage> ); }
  if (activeSosExists) { return <ButtonSos3 />; }

  return (
    <IonPage>
      <HeaderSos pageName="Botón SOS" />
      <IonContent className="ion-padding sos-page-content">
        {currentView === 'INITIAL_VIEW' && renderInitialSosView()}
        {currentView === 'COUNTDOWN_VIEW' && renderCountdownActionView()}
        {currentView === 'SUBMITTING_VIEW' && renderSubmittingView()}
      </IonContent>
      <Footer />
    </IonPage>
  );
};

export default SOS;